import { Component } from '@theme/component';

/**
 * @typedef {Object} TabsRefs
 * @property {HTMLElement[]} tabs
 */

/**
 * Generic ARIA tabs. Panels are linked by each tab's `aria-controls` value.
 * EW custom: `data-tab-label` syncs labels; `data-tab-pending` hides pending tabs.
 * @extends {Component<TabsRefs>}
 */
export class TabsComponent extends Component {
  #active = 0;

  /** @type {AbortController | null} */
  #abort = null;

  /** @type {MutationObserver[]} */
  #panelObservers = [];

  connectedCallback() {
    super.connectedCallback();
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    this.#active = Math.max(
      0,
      tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );

    for (const tab of tabs) {
      const panel = this.#panelFor(tab);
      if (!panel) continue;
      panel.setAttribute('role', 'tabpanel');
      if (!panel.hasAttribute('aria-labelledby') && tab.id) {
        panel.setAttribute('aria-labelledby', tab.id);
      }
    }

    this.#setupEventListeners();
    this.#observePanels();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    for (const observer of this.#panelObservers) observer.disconnect();
    this.#panelObservers = [];
  }

  /**
   * @param {HTMLElement} tab
   * @returns {HTMLElement | null}
   */
  #panelFor(tab) {
    const id = tab.getAttribute('aria-controls');
    return id ? this.querySelector(`#${CSS.escape(id)}`) : null;
  }

  #setupEventListeners() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };
    const { tabs } = this.refs;

    this.addEventListener('keydown', (e) => this.#handleKeydown(e), opts);

    for (const [i, tab] of tabs.entries()) {
      tab.addEventListener('click', (e) => this.#handleTabClick(e, i), opts);
      tab.addEventListener('focus', (e) => this.#handleTabFocus(e, i), opts);
    }
  }

  /** @param {KeyboardEvent} e */
  #handleKeydown(e) {
    const target = /** @type {HTMLElement} */ (e.target);
    if (target.getAttribute('role') !== 'tab') return;

    const visible = this.#visibleTabs();
    const i = visible.indexOf(target);
    if (i === -1) return;

    const navMap = {
      ArrowLeft: -1,
      ArrowRight: 1,
      Home: -i,
      End: visible.length - 1 - i,
    };

    const offset = navMap[/** @type {keyof typeof navMap} */ (e.key)];
    if (offset === undefined) return;

    e.preventDefault();
    const nextTab = visible[(i + offset + visible.length) % visible.length];
    if (!nextTab) return;

    nextTab.focus();
    this.#activate(this.refs.tabs.indexOf(nextTab));
  }

  /**
   * @param {MouseEvent} e
   * @param {number} index
   */
  #handleTabClick(e, index) {
    e.preventDefault();
    this.#activate(index);
  }

  /**
   * @param {FocusEvent} e
   * @param {number} index
   */
  #handleTabFocus(e, index) {
    if (/** @type {HTMLElement} */ (e.target).matches(':focus-visible')) this.#activate(index);
  }

  /** @returns {HTMLElement[]} */
  #visibleTabs() {
    return (this.refs.tabs ?? []).filter((tab) => !tab.hidden);
  }

  /** @param {number} index */
  #activate(index) {
    const { tabs } = this.refs;
    if (!tabs?.[index] || tabs[index].hidden || index === this.#active) return;
    this.#active = index;
    this.#render();
  }

  #render() {
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    for (const [i, tab] of tabs.entries()) {
      const isActive = i === this.#active;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');

      const panel = this.#panelFor(tab);
      if (panel) {
        // Inactive panels are hidden visually only (not with `display: none`), so
        // elements inside them that lazy-load via IntersectionObserver (like
        // `<product-recommendations>`) still see themselves as "visible" and load
        // instead of waiting forever for a viewport entry that never happens.
        panel.style.position = isActive ? '' : 'absolute';
        panel.style.inset = isActive ? '' : '0';
        panel.style.visibility = isActive ? '' : 'hidden';
        panel.style.pointerEvents = isActive ? '' : 'none';
      }
    }

    const activeTab = tabs[this.#active];
    if (!activeTab) return;

    this.dispatchEvent(
      new CustomEvent('tabs:change', {
        bubbles: true,
        detail: { index: this.#active, tab: activeTab, panel: this.#panelFor(activeTab) },
      })
    );
  }

  /**
   * Watches each panel for its own `hidden` attribute changing - the generic signal
   * a panel uses to say "I have nothing to show" - and mirrors it onto the matching tab.
   */
  #observePanels() {
    const { tabs } = this.refs;
    for (const observer of this.#panelObservers) observer.disconnect();
    this.#panelObservers = [];

    for (const tab of tabs) {
      const panel = this.#panelFor(tab);
      if (!panel) continue;

      this.#syncTab(tab, panel);

      const observer = new MutationObserver(() => {
        this.#syncTab(tab, panel);
        this.#reconcileActiveTab();
      });
      observer.observe(panel, { attributes: true, attributeFilter: ['hidden', 'data-tab-label', 'data-tab-pending'] });
      this.#panelObservers.push(observer);
    }

    this.#reconcileActiveTab();
  }

  /**
   * Mirrors a panel's availability and optional custom label onto its tab.
   *
   * @param {HTMLElement} tab
   * @param {HTMLElement} panel
   */
  #syncTab(tab, panel) {
    // EW custom: Avoid a pending tab flashing before its availability is confirmed.
    tab.hidden = panel.hidden || panel.hasAttribute('data-tab-pending');
    if (panel.dataset.tabLabel) tab.textContent = panel.dataset.tabLabel;
  }

  #reconcileActiveTab() {
    const { tabs } = this.refs;
    const activeTab = tabs[this.#active];
    if (!activeTab || activeTab.hidden) {
      const next = this.#visibleTabs()[0];
      this.#active = next ? tabs.indexOf(next) : -1;
      this.#render();

      // Keep keyboard focus in the tablist when the focused tab becomes unavailable.
      if (next && document.activeElement === activeTab) next.focus();
      return;
    }

    this.#render();
  }
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}
